import {
    CoinType,
    getPeripheryStatusColor,
    HopperCoinCount,
    OperatorZoneBarrierGatePeriphery,
    OperatorZoneBarrierGatePeripheryDetails,
    PayStationDetailInfo,
    PeripheryInfoType,
} from './CloudConnectorPeripheriesOverview.tsx';
import {
    generatePath,
    Outlet,
    useNavigate,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import {
    CloudConnectorPeripheryEvent,
    CloudConnectorPeripheryEventType,
    CloudConnectorPeripheryType,
    getCloudConnectorEventTypeTranslation,
    getMainEventfromEvents,
    getPeripheryStatefromMainCategory,
} from 'dg-web-shared/model/CloudConnector.ts';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { useIsMobileRoute } from '../layout/components/BreadCrumb.tsx';
import {
    Box,
    Button,
    FormControlLabel,
    InputLabel,
    Stack,
    Switch,
    Tooltip,
} from '@mui/material';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { Edit, KeyboardControlKey } from '@mui/icons-material';
import { HopperConfigType } from 'dg-web-shared/model/ZoneConfiguration.ts';
import { numberToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import { ReactNode, useEffect, useState } from 'react';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { useTheme } from '@mui/material/styles';
import {
    CloudConnectorCommandType,
    isPeripheryDisconnected,
    RequestErrorAlert,
    usePeripheryCommand,
} from './CloudConnectorPeripheryDetailTabs.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { MaterialTab } from '../layout/components/MaterialTab.tsx';
import { ParkingPortalButton } from '../mobile/layout/ParkingPortalButton.tsx';
import InfoIcon from '@mui/icons-material/Info';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { ConfirmPermanentlyOpenBarrier } from './CloudConnectorPeripheryDetailTabStatus.tsx';

function isCloudConnectorPeripheryType(
    value?: string,
): value is CloudConnectorPeripheryType {
    return Object.values(CloudConnectorPeripheryType).includes(
        value as CloudConnectorPeripheryType,
    );
}

export function getCloudConnectorPeripheryType(
    value?: string,
): CloudConnectorPeripheryType | null {
    if (isCloudConnectorPeripheryType(value)) {
        return value;
    }
    return null;
}

export function usePeripheryDetailsOutlet() {
    return useOutletContext<PeripheryDetailsOutletContext>();
}

export function PeripheryDetailOutlet({
    context,
}: {
    context: PeripheryDetailsOutletContext;
}) {
    return <Outlet context={context} />;
}

interface PeripheryDetailsOutletContext {
    peripheryConfig: OperatorZoneBarrierGatePeripheryDetails;
    refetchCloudConnectorPeriphery: () => void;
}

export function PeripheryDetailWrapper({
    peripheryType,
    render,
}: {
    peripheryType?: CloudConnectorPeripheryType;
    render: (context: PeripheryDetailsOutletContext) => ReactNode;
}) {
    const urlParams = usePeripheryDetailUrlParams();
    const operatorId = useOperatorContext().currentLogin.mandantId;
    const [refetchCounter, setRefetchCounter] = useState(0);

    const [peripheryDetailRequestState, refetchPeripheryDetail] =
        useServerFetch<
            OperatorZoneBarrierGatePeripheryDetails,
            {
                operatorId: number;
                zoneId: number;
                peripheryType: CloudConnectorPeripheryType;
                onPremiseId: number;
                refetchCounter: number;
            }
        >(
            context => ({
                url: `/ui-api/operator-account/${context.operatorId}/zone-barrier-gate/${context.zoneId}/periphery/${context.peripheryType}/${context.onPremiseId}`,
            }),
            urlParams
                ? {
                      operatorId: operatorId,
                      zoneId: urlParams.zoneId,
                      peripheryType: peripheryType ?? urlParams.peripheryType!,
                      onPremiseId: urlParams.onPremiseId,
                      refetchCounter: refetchCounter,
                  }
                : null,
        );
    const isMobile = useIsMobileRoute();

    useEffect(() => {
        if (
            peripheryDetailRequestState.status === RequestStatus.SUCCESS ||
            peripheryDetailRequestState.status === RequestStatus.ERROR
        ) {
            const timeout = setTimeout(() => {
                setRefetchCounter(prevCount => prevCount + 1);
            }, 3000);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [peripheryDetailRequestState.status, refetchCounter]);

    return (
        <OperatorRoutedModal
            variant={isMobile ? ModalVariant.MOBILE : ModalVariant.SMALL}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={peripheryDetailRequestState}
                    render={peripheryConfig => (
                        <ParkingPortalLayoutWithHeader
                            title={peripheryConfig.name}
                            backTo={'..'}
                            scrollable
                        >
                            {render({
                                peripheryConfig: peripheryConfig,
                                refetchCloudConnectorPeriphery:
                                    refetchPeripheryDetail,
                            })}
                        </ParkingPortalLayoutWithHeader>
                    )}
                    spinnerFreeRefresh
                />
            )}
        />
    );
}

export function PayStationDetailsLayout({
    context,
}: {
    context: PeripheryDetailsOutletContext;
}) {
    return (
        <>
            <MaterialTab
                tabs={[
                    {
                        label: (
                            <Localized
                                de="Details"
                                fr="Détails"
                                it="Dettagli"
                                en="Details"
                            />
                        ),
                        disabled: false,
                        to: 'detail',
                    },
                    {
                        label: (
                            <Localized
                                de="Zahlungen"
                                fr="Paiements"
                                it="Pagamenti"
                                en="Payments"
                            />
                        ),
                        disabled: false,
                        to: 'payments',
                    },
                ]}
            />
            <PeripheryDetailOutlet context={context} />
        </>
    );
}

export function PeripheryDetailsLayout({
    context,
}: {
    context: PeripheryDetailsOutletContext;
}) {
    return (
        <>
            <MaterialTab
                tabs={[
                    {
                        label: (
                            <Localized
                                de="Präsenz"
                                fr="Présence"
                                it="Presenza"
                                en="Presence"
                            />
                        ),
                        disabled:
                            isPeripheryDisconnected(context.peripheryConfig) ||
                            context.peripheryConfig.withoutPresence == true,
                        to: 'presence',
                    },
                    ...(context.peripheryConfig.payments != null
                        ? [
                              {
                                  label: (
                                      <Localized
                                          de="Zahlungen"
                                          fr="Paiements"
                                          it="Pagamenti"
                                          en="Payments"
                                      />
                                  ),
                                  disabled: false,
                                  to: 'payments',
                              },
                          ]
                        : []),
                    {
                        label: (
                            <Localized
                                de="Details"
                                fr="Détails"
                                it="Dettagli"
                                en="Details"
                            />
                        ),
                        disabled: false,
                        to: 'detail',
                    },
                ]}
            />
            <PeripheryDetailOutlet context={context} />
        </>
    );
}

export function PayStationDetailsTab() {
    const context = usePeripheryDetailsOutlet();
    return (
        <Stack
            direction={'column'}
            sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'flex-end',
                padding: theme => theme.spacing(3),
            }}
        >
            <PeripheryInfo peripheryConfig={context.peripheryConfig} />
            <PeripheryDetailOutlet context={context} />
        </Stack>
    );
}

export function AccessDetails({
    context,
}: {
    context: PeripheryDetailsOutletContext;
}) {
    const { currentLogin } = useOperatorContext();
    const peripheryConfig = context.peripheryConfig;
    const isDisconnected = isPeripheryDisconnected(peripheryConfig);
    const [peripheryCommandRequestState, executePeripheryCommand] =
        usePeripheryCommand();
    const [openPermanentlyOpenModal, setOpenPermanentlyOpenModal] =
        useState(false);
    const barrierPermanentlyOpen = peripheryConfig.events.some(
        event =>
            event.eventType ===
            CloudConnectorPeripheryEventType.BARRIER_PERMANENTLY_OPEN,
    );
    useServerErrorEffect(peripheryCommandRequestState, () =>
        setOpenPermanentlyOpenModal(false),
    );
    useServerSuccessEffect(peripheryCommandRequestState, () =>
        setOpenPermanentlyOpenModal(false),
    );
    return (
        <Stack
            direction={'column'}
            sx={{
                height: '100%',
                display: 'flex',
                flexGrow: 1,
                overflow: 'auto',
                justifyContent: 'space-between',
                padding: theme => theme.spacing(3),
            }}
        >
            <Stack spacing={2}>
                <PeripheryEventInfos events={peripheryConfig.events} />
                {!peripheryConfig.withoutPresence && (
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Barriere"
                                fr="Barrière"
                                it="Barriera"
                                en="Barrier"
                            />
                        }
                        value={
                            <FormControlLabel
                                label={
                                    <Box
                                        sx={{
                                            color: theme =>
                                                theme.palette.primary.main,
                                            ...Typo.caption,
                                        }}
                                    >
                                        <Localized
                                            de="dauerhaft offen"
                                            fr="ouverte en permanence"
                                            it="permanentemente aperta"
                                            en="permanently open"
                                        />
                                    </Box>
                                }
                                control={
                                    <Switch
                                        checked={barrierPermanentlyOpen}
                                        disabled={
                                            peripheryConfig.manualClose ==
                                                true ||
                                            peripheryCommandRequestState.status ===
                                                RequestStatus.PENDING ||
                                            isDisconnected
                                        }
                                        onChange={(_e, checked) => {
                                            if (!checked) {
                                                executePeripheryCommand({
                                                    operatorId:
                                                        currentLogin.mandantId,
                                                    zoneId: peripheryConfig.zoneId,
                                                    peripheryType:
                                                        peripheryConfig.peripheryType,
                                                    onPremiseId:
                                                        peripheryConfig.onPremiseId,
                                                    commandType:
                                                        CloudConnectorCommandType.BARRIER_PERMANENTLY_OPEN,
                                                    secondaryGate: null,
                                                    permanentlyOpen: false,
                                                    forceOutOfOrder: null,
                                                    zoneBarrierGateEntryWithoutExitId:
                                                        null,
                                                });
                                            } else {
                                                setOpenPermanentlyOpenModal(
                                                    true,
                                                );
                                            }
                                        }}
                                    />
                                }
                            />
                        }
                    />
                )}
                <ConfirmPermanentlyOpenBarrier
                    open={openPermanentlyOpenModal}
                    onConfirm={() =>
                        executePeripheryCommand({
                            operatorId: currentLogin.mandantId,
                            zoneId: peripheryConfig.zoneId,
                            peripheryType: peripheryConfig.peripheryType,
                            onPremiseId: peripheryConfig.onPremiseId,
                            commandType:
                                CloudConnectorCommandType.BARRIER_PERMANENTLY_OPEN,
                            secondaryGate: null,
                            permanentlyOpen: true,
                            forceOutOfOrder: null,
                            zoneBarrierGateEntryWithoutExitId: null,
                        })
                    }
                    onCancel={() => setOpenPermanentlyOpenModal(false)}
                    isExit={false}
                />
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    paddingTop: theme => theme.spacing(2),
                }}
            >
                <RequestErrorAlert
                    open={
                        peripheryCommandRequestState.status ===
                        RequestStatus.ERROR
                    }
                />
                {!isDisconnected && (
                    <ParkingPortalButton
                        onClick={() =>
                            executePeripheryCommand({
                                operatorId: currentLogin.mandantId,
                                zoneId: peripheryConfig.zoneId,
                                peripheryType: peripheryConfig.peripheryType,
                                onPremiseId: peripheryConfig.onPremiseId,
                                commandType:
                                    CloudConnectorCommandType.BARRIER_UP_ONCE,
                                secondaryGate: false,
                                permanentlyOpen: null,
                                forceOutOfOrder: null,
                                zoneBarrierGateEntryWithoutExitId: null,
                            })
                        }
                        disabled={
                            peripheryCommandRequestState.status ===
                            RequestStatus.PENDING
                        }
                        icon={<KeyboardControlKey />}
                    >
                        <Localized
                            de="Öffnen (1x)"
                            fr="Ouvrir (1x)"
                            it="Aprire (1x)"
                            en="Open (1x)"
                        />
                    </ParkingPortalButton>
                )}
            </Box>
        </Stack>
    );
}

export function PeripheryInfo({
    peripheryConfig,
}: {
    peripheryConfig: OperatorZoneBarrierGatePeriphery;
}) {
    const { currentLogin } = useOperatorContext();
    const [peripheryCommandRequestState, executePeripheryCommand] =
        usePeripheryCommand();
    const [openForceOutOfOrderModal, setOpenForceOutOfOrderModal] =
        useState(false);
    useServerErrorEffect(peripheryCommandRequestState, () =>
        setOpenForceOutOfOrderModal(false),
    );
    useServerSuccessEffect(peripheryCommandRequestState, () => {
        setOpenForceOutOfOrderModal(false);
    });

    const isForcedOutOfOrder = peripheryConfig.events.some(
        event =>
            event.eventType ===
            CloudConnectorPeripheryEventType.FORCED_OUT_OF_ORDER,
    );
    const isForceOutOfOrderDisabled = peripheryConfig.events.some(
        event =>
            event.eventType === CloudConnectorPeripheryEventType.DISCONNECTED ||
            event.eventType === CloudConnectorPeripheryEventType.OUT_OF_ORDER,
    );

    if (peripheryConfig.info == null) {
        return <Box sx={{ display: 'flex', flexGrow: 1 }} />;
    }
    switch (peripheryConfig.info.peripheryInfoType) {
        case PeripheryInfoType.TICKET_REMAINING:
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        textTransform: 'uppercase',
                    }}
                >
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Verbleibende Tickets"
                                fr="Tickets restants"
                                it="Ticket restanti"
                                en="Remaining tickets"
                            />
                        }
                        value={peripheryConfig.info.ticketRemaining}
                    />
                </Box>
            );
        case PeripheryInfoType.PAY_STATION_INFO: {
            return (
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    {!peripheryConfig.info.isCashless && (
                        <HopperCoinCountInfo
                            payStationDetailInfo={peripheryConfig.info}
                        />
                    )}
                    <PeripheryEventInfos events={peripheryConfig.events} />
                    {!peripheryConfig.info.isCashless && (
                        <>
                            <ReadOnlyTextField
                                label={
                                    <>
                                        <Localized
                                            de="Münztresor (Wert/Gewicht)"
                                            fr="Boîte monnaie (valeur/poids)"
                                            it="Cassetta monete (valore/peso)"
                                            en="Coin box (value/weight)"
                                        />
                                        <Tooltip
                                            title={
                                                <Localized
                                                    de="Warnung «fast voll», wenn 15 kg überschritten werden"
                                                    fr="Avertissement «presque plein» lorsque les 15 kg sont dépassés"
                                                    it="Avviso «quasi piena» quanto vengono superati i 15 kg"
                                                    en="Warning «almost full» when 15 kg is exceeded"
                                                />
                                            }
                                            placement="top"
                                            enterTouchDelay={0}
                                        >
                                            <InfoIcon
                                                sx={{
                                                    color: theme =>
                                                        theme.palette.blue.main,
                                                    marginLeft: 1,
                                                }}
                                            />
                                        </Tooltip>
                                    </>
                                }
                                value={`CHF ${peripheryConfig.info.cashboxTotalMoney.toFixed(
                                    2,
                                )} / ${peripheryConfig.info.cashboxWeightKg} kg`}
                            />
                            <ReadOnlyTextField
                                label={
                                    <>
                                        <Localized
                                            de="Notentresor (Wert/Anzahl)"
                                            fr="Boîte à billets (valeur/quantité)"
                                            it="Cassetta banconote (valore/quantità)"
                                            en="Banknotes box (value/quantity)"
                                        />
                                        <Tooltip
                                            title={
                                                <Localized
                                                    de="Warnung «fast voll», wenn 320 Noten überschritten werden"
                                                    fr="Avertissement «presque plein» lorsque 320 billets sont dépassés"
                                                    it="Avviso «quasi piena» quanto vengono superate le 320 banconote"
                                                    en="Warning «almost full» when 320 bills are exceeded"
                                                />
                                            }
                                            placement="top"
                                            enterTouchDelay={0}
                                        >
                                            <InfoIcon
                                                sx={{
                                                    color: theme =>
                                                        theme.palette.blue.main,
                                                    marginLeft: 1,
                                                }}
                                            />
                                        </Tooltip>
                                    </>
                                }
                                value={`CHF ${peripheryConfig.info.billCollectorTotalMoney} / ${peripheryConfig.info.billCount}
                                            x`}
                            />
                        </>
                    )}
                    <FormControlLabel
                        label={
                            <Localized
                                de="manuell geschlossen"
                                fr="fermée manuellement"
                                it="chiusa manualmente"
                                en="manually closed"
                            />
                        }
                        control={
                            <Switch
                                checked={isForcedOutOfOrder}
                                disabled={
                                    isForceOutOfOrderDisabled ||
                                    peripheryCommandRequestState.status ===
                                        RequestStatus.PENDING
                                }
                                onChange={(_e, checked) => {
                                    if (!checked) {
                                        executePeripheryCommand({
                                            operatorId: currentLogin.mandantId,
                                            zoneId: peripheryConfig.zoneId,
                                            peripheryType:
                                                peripheryConfig.peripheryType,
                                            onPremiseId:
                                                peripheryConfig.onPremiseId,
                                            commandType:
                                                CloudConnectorCommandType.FORCE_OUT_OF_ORDER,
                                            secondaryGate: null,
                                            permanentlyOpen: null,
                                            forceOutOfOrder: false,
                                            zoneBarrierGateEntryWithoutExitId:
                                                null,
                                        });
                                    } else {
                                        setOpenForceOutOfOrderModal(true);
                                    }
                                }}
                            />
                        }
                    />

                    <ConfirmForceOutOfOrderModal
                        open={openForceOutOfOrderModal}
                        onConfirm={() =>
                            executePeripheryCommand({
                                operatorId: currentLogin.mandantId,
                                zoneId: peripheryConfig.zoneId,
                                peripheryType: peripheryConfig.peripheryType,
                                onPremiseId: peripheryConfig.onPremiseId,
                                commandType:
                                    CloudConnectorCommandType.FORCE_OUT_OF_ORDER,
                                secondaryGate: null,
                                permanentlyOpen: null,
                                forceOutOfOrder: true,
                                zoneBarrierGateEntryWithoutExitId: null,
                            })
                        }
                        onCancel={() => setOpenForceOutOfOrderModal(false)}
                    />
                </Stack>
            );
        }
        case PeripheryInfoType.HAS_EFT_TERMINAL:
            return <Box sx={{ display: 'flex', flexGrow: 1 }} />;
    }
}

function ConfirmForceOutOfOrderModal({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Kasse sperren"
                    fr="Bloquer la caisse"
                    it="Bloccare la cassa"
                    en="Lock pay station"
                />
            }
            onCancel={onCancel}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onConfirm={onConfirm}
            confirmLabel={
                <Localized de="Sperren" fr="Bloquer" it="Blocca" en="Lock" />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie die Kasse wirklich manuell sperren?"
                    fr="Voulez-vous vraiment bloquer manuellement la caisse?"
                    it="Vuole davvero bloccare manualmente la cassa?"
                    en="Do you really want to manually lock the pay station?"
                />
            </p>
        </ConfirmationDialog>
    );
}

function HopperCoinCountInfo({
    payStationDetailInfo,
}: {
    payStationDetailInfo: PayStationDetailInfo;
}) {
    return (
        <Box>
            <InputLabel
                sx={{
                    lineHeight: '1em',
                    paddingTop: '1px',
                    textTransform: 'uppercase',
                }}
            >
                <Localized
                    de="Münzstand"
                    fr="État de la monnaie"
                    it="Stato moneta"
                    en="Coin state"
                />
            </InputLabel>
            <Stack direction={'column'} spacing={1}>
                <Stack direction={'row'} spacing={1}>
                    <HopperCoinCountBox
                        payStationDetailInfo={payStationDetailInfo}
                        hopperPosition={HopperPosition.TOP_LEFT}
                    />
                    <HopperCoinCountBox
                        payStationDetailInfo={payStationDetailInfo}
                        hopperPosition={HopperPosition.TOP_RIGHT}
                    />
                </Stack>
                <Stack direction={'row'} spacing={1}>
                    <HopperCoinCountBox
                        payStationDetailInfo={payStationDetailInfo}
                        hopperPosition={HopperPosition.BOTTOM_LEFT}
                    />
                    <HopperCoinCountBox
                        payStationDetailInfo={payStationDetailInfo}
                        hopperPosition={HopperPosition.BOTTOM_RIGHT}
                    />
                </Stack>
            </Stack>
        </Box>
    );
}

function HopperCoinCountBox({
    payStationDetailInfo,
    hopperPosition,
}: {
    payStationDetailInfo: PayStationDetailInfo;
    hopperPosition: HopperPosition;
}) {
    const navigate = useNavigate();
    const coinCount = getCoinCountByPosition(
        payStationDetailInfo,
        hopperPosition,
    );
    return (
        <Box
            sx={theme => ({
                color: theme.palette.white.main,
                backgroundColor: theme.palette.primary.main,
                padding: theme.spacing(1.4),
                fontWeight: 700,
                flexGrow: 1,
                aspectRatio: 1.5,
                display: 'flex',
                flexDirection: 'column',
            })}
        >
            {coinCount && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            fontSize: '28px',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {`${coinCount.numberOfCoins}x`}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            fontSize: '16px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box>
                            {numberToLocalPrice(coinCount.chfDenomination)}
                        </Box>
                        <Button
                            sx={{
                                color: theme => theme.palette.white.main,
                                padding: 0,
                                minWidth: '24px !important',
                            }}
                            onClick={() =>
                                navigate(
                                    generatePath(
                                        'coin-balance/:coinType/manual-correction',
                                        { coinType: coinCount.coinType },
                                    ),
                                )
                            }
                        >
                            <Edit
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                }}
                            />
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

enum HopperPosition {
    TOP_LEFT = 'TOP_LEFT',
    TOP_RIGHT = 'TOP_RIGHT',
    BOTTOM_LEFT = 'BOTTOM_LEFT',
    BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

function getCoinCountByPosition(
    info: PayStationDetailInfo,
    hopperPosition: HopperPosition,
): HopperCoinCount | null {
    switch (info.hopperConfigType) {
        case HopperConfigType.CASHLESS:
            return null;
        case HopperConfigType.NRI_50_200:
            return getNRI_50_200CoinCountByPosition(info, hopperPosition);
        case HopperConfigType.NRI_50_100:
            return getNRI_50_100CoinCountByPosition(info, hopperPosition);
        case HopperConfigType.NRI_100_200:
            return getNRI_100_200CoinCountByPosition(info, hopperPosition);
        case HopperConfigType.NRI_50_100_200:
            return getNRI_50_100_200CoinCountByPosition(info, hopperPosition);
    }
}

function getNRI_50_200CoinCountByPosition(
    info: PayStationDetailInfo,
    hopperPosition: HopperPosition,
): HopperCoinCount | null {
    switch (hopperPosition) {
        case HopperPosition.TOP_LEFT:
            return getByCoinType(info, CoinType.CHF_200);
        case HopperPosition.TOP_RIGHT:
            return getByCoinType(info, CoinType.CHF_500);
        case HopperPosition.BOTTOM_LEFT:
            return getByCoinType(info, CoinType.CHF_10);
        case HopperPosition.BOTTOM_RIGHT:
            return getByCoinType(info, CoinType.CHF_50);
    }
}

function getNRI_50_100CoinCountByPosition(
    info: PayStationDetailInfo,
    hopperPosition: HopperPosition,
): HopperCoinCount | null {
    switch (hopperPosition) {
        case HopperPosition.TOP_LEFT:
            return getByCoinType(info, CoinType.CHF_100);
        case HopperPosition.TOP_RIGHT:
            return getByCoinType(info, CoinType.CHF_500);
        case HopperPosition.BOTTOM_LEFT:
            return getByCoinType(info, CoinType.CHF_10);
        case HopperPosition.BOTTOM_RIGHT:
            return getByCoinType(info, CoinType.CHF_50);
    }
}

function getNRI_100_200CoinCountByPosition(
    info: PayStationDetailInfo,
    hopperPosition: HopperPosition,
): HopperCoinCount | null {
    switch (hopperPosition) {
        case HopperPosition.TOP_LEFT:
            return getByCoinType(info, CoinType.CHF_200);
        case HopperPosition.TOP_RIGHT:
            return getByCoinType(info, CoinType.CHF_500);
        case HopperPosition.BOTTOM_LEFT:
            return getByCoinType(info, CoinType.CHF_10);
        case HopperPosition.BOTTOM_RIGHT:
            return getByCoinType(info, CoinType.CHF_100);
    }
}

function getNRI_50_100_200CoinCountByPosition(
    info: PayStationDetailInfo,
    hopperPosition: HopperPosition,
): HopperCoinCount | null {
    switch (hopperPosition) {
        case HopperPosition.TOP_LEFT:
            return getByCoinType(info, CoinType.CHF_100);
        case HopperPosition.TOP_RIGHT:
            return getByCoinType(info, CoinType.CHF_200);
        case HopperPosition.BOTTOM_LEFT:
            return getByCoinType(info, CoinType.CHF_10);
        case HopperPosition.BOTTOM_RIGHT:
            return getByCoinType(info, CoinType.CHF_50);
    }
}

function getByCoinType(info: PayStationDetailInfo, coinType: CoinType) {
    return info.hopperCoinCount.find(c => c.coinType == coinType) ?? null;
}

function usePeripheryDetailUrlParams(): {
    zoneId: number;
    peripheryType: CloudConnectorPeripheryType | null;
    onPremiseId: number;
} | null {
    const urlParams = useParams<{
        zoneId: string;
        peripheryType: string;
        onPremiseId: string;
    }>();
    const zoneId = urlParams.zoneId ? parseInt(urlParams.zoneId, 10) : null;
    const peripheryType = getCloudConnectorPeripheryType(
        urlParams.peripheryType,
    );
    const onPremiseId = urlParams.onPremiseId
        ? parseInt(urlParams.onPremiseId, 10)
        : null;
    if (zoneId && onPremiseId) {
        return {
            zoneId,
            peripheryType,
            onPremiseId,
        };
    }
    return null;
}

export function PeripheryEventInfos({
    events,
}: {
    events: CloudConnectorPeripheryEvent[];
}) {
    const theme = useTheme();
    const { language } = useLanguage();
    const mainEvent = getMainEventfromEvents(events);
    const outOfOrderInfo = getOutOfOrderInfo(events);
    const warningInfo = getWarningInfo(events);
    return (
        <Box>
            <ReadOnlyTextField
                sx={{
                    textTransform: 'uppercase',
                    color: theme =>
                        getPeripheryStatusColor(
                            getPeripheryStatefromMainCategory(
                                mainEvent?.category ?? null,
                            ),
                            theme,
                        ),
                }}
                label={
                    <Localized de="Status" fr="Status" it="Stato" en="State" />
                }
                value={
                    <Localized
                        {...getCloudConnectorEventTypeTranslation(
                            mainEvent?.eventType ?? null,
                        )}
                    />
                }
            />
            <Box sx={{ paddingTop: theme => theme.spacing(2) }}>
                <ReadOnlyTextField
                    sx={{
                        textTransform: 'uppercase',
                    }}
                    label={
                        <Localized
                            de="Probleme"
                            fr="Problèmes"
                            it="Problemi"
                            en="Problems"
                        />
                    }
                    value={
                        outOfOrderInfo || warningInfo ? (
                            <>
                                {outOfOrderInfo && (
                                    <div
                                        style={{
                                            whiteSpace: 'pre-line',
                                            color: theme.palette.error.main,
                                        }}
                                    >
                                        {outOfOrderInfo[language]}
                                    </div>
                                )}
                                {warningInfo && (
                                    <div
                                        style={{
                                            whiteSpace: 'pre-line',
                                            color: theme.palette.warning.main,
                                        }}
                                    >
                                        {warningInfo[language]}
                                    </div>
                                )}
                            </>
                        ) : (
                            EmDash
                        )
                    }
                />
            </Box>
        </Box>
    );
}

function getOutOfOrderInfo(
    events: CloudConnectorPeripheryEvent[],
): Message | null {
    const eventTypesWithOutOfOrderInfo = [
        CloudConnectorPeripheryEventType.OUT_OF_ORDER,
        CloudConnectorPeripheryEventType.FORCED_OUT_OF_ORDER,
    ];
    return (
        events.find(event =>
            eventTypesWithOutOfOrderInfo.some(type => type === event.eventType),
        )?.outOfOrderInfo ?? null
    );
}

function getWarningInfo(
    events: CloudConnectorPeripheryEvent[],
): Message | null {
    return (
        events.find(
            event =>
                event.eventType === CloudConnectorPeripheryEventType.WARNING,
        )?.warnings ?? null
    );
}
